/**
 * 需要想定 daily 画面の page-model (アーキ改善 C3)。
 *
 * 従来 index.tsx の render body に散っていた「決定ロジック」を単一の deep な
 * hook に集約する:
 * - 編集 selection state (targetScope / selectedRetailerBusinessCd / dragMode) と
 *   未保存ガード経由の切替
 * - gate 群 (`deriveForecastGates` / `deriveRetailerScope`、pure fn へ委譲)
 * - 過去日 write-seam の強制 (canMutate を index から撤去し hook 内に閉じる)
 * - save / dirty の BG_TOTAL fallback
 * - write orchestration (保存 / 保存して移動 / 確定 / 衝突解決 / 破棄して移動)
 * - 確定ダイアログ・rescale モーダルの UI 状態
 *
 * editor (`useForecastDraftEditor`) / 離脱ガード (`useUnsavedGuard`) /
 * 確定 mutation (`useConfirmDemandForecast`) を配下に compose する。index は
 * input を解決してこの hook を呼び、返り値を view へ配線するだけになる
 * (deletion test: 本 hook を消すと決定が index に再集中する)。
 *
 * 過去日 write-gate: canMutate は index から撤去し、本 hook 内の 3 つの controlled
 * 箇所にのみ閉じる (旧: index の 4 seam に分散していた convention 依存を排除):
 * - 表示 gate: `canMutateTargetDate` flag (毎レンダー実時刻で再評価)。index は
 *   この flag のみ消費する (write-seam と表示 gate の分離を維持)。
 * - write orchestration 入口 (保存 / 保存して移動) は `guardWrite` HOF 経由で構築し
 *   過去日を no-op / cancel にする。write-gate の primary constructor。
 * - 確定 (`handleConfirmExecute`) は areaCode/targetDate の precondition を先に
 *   評価する順序制約があるため guardWrite ではなく同一 predicate を inline で呼ぶ
 *   (この 1 箇所だけ HOF ではなく inline、順序制約の帰結)。
 * なお破棄して移動 (`handleDiscardAndLeave`) と衝突解決 (`handleResolveConflict`) は
 * 値変更を伴わないため意図的に unguarded。editor 内部 (`canMutateNow`) は buffer
 * 書込の二重防御としてそのまま併存する。
 */
import type { Dispatch, SetStateAction } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { clone, create } from '@bufbuild/protobuf'
import { timestampDate } from '@bufbuild/protobuf/wkt'
import { ConnectError } from '@connectrpc/connect'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type {
  ConfirmDemandForecastRequest,
  DemandForecastDailyPage,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import {
  ConfirmDemandForecastRequestSchema,
  DemandForecastConfirmBlock,
  DemandForecastConfirmErrorReason,
  DemandForecastConfirmErrorReasonSchema,
  ManualConfirmationBlock,
  RetailerConfirmTargetSchema,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import type { WorkflowSection } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import { useTranslation } from 'react-i18next'

import { useSuccessToast } from '~/hooks/useSuccessToast'
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard'
import { readAppErrorInfo } from '~/libs/appErrorInfo'
import { appToaster } from '~/libs/toaster'
import { now as serverNow } from '~/models/clock'
import { useConfirmDemandForecast } from '~/pages/DemandForecastDaily/hooks/useConfirmDemandForecast'
import {
  type SaveAllResult,
  UNATTRIBUTED_SAVE_STATE_KEY,
} from '~/pages/DemandForecastDaily/hooks/useDraftSaveQueue'
import type {
  ForecastEditorViewModel,
  RefetchDemandForecastDailyPageResult,
} from '~/pages/DemandForecastDaily/hooks/useForecastDraftEditor'
import { useForecastDraftEditor } from '~/pages/DemandForecastDaily/hooks/useForecastDraftEditor'
import {
  useBreakdownTabSelection,
  useRetailerBreakdown,
} from '~/pages/DemandForecastDaily/hooks/useRetailerBreakdown'
import { canMutate } from '~/pages/DemandForecastDaily/models/canMutate'
import { editUnitKey } from '~/pages/DemandForecastDaily/models/editUnits'
import type { ForecastGates } from '~/pages/DemandForecastDaily/models/forecastGates'
import {
  deriveForecastGates,
  deriveRetailerScope,
  hasGroupForecastRetailer,
} from '~/pages/DemandForecastDaily/models/forecastGates'
import {
  BREAKDOWN_TOTAL_TAB,
  type BreakdownLeafView,
  type RetailerBreakdownView,
  breakdownLeavesFromPage,
  joinBreakdownActuals,
} from '~/pages/DemandForecastDaily/models/retailerBreakdownView'
import { formatProtoDate } from '~/pages/DemandForecastDaily/models/sectionHelpers'
import { resolveAggregatePreviewDetailedErrorMessage } from '~/pages/DemandForecastDaily/models/serverError'
import {
  collectNegativeKomas,
  formatLimitViolationLines,
  formatNegativeWarning,
} from '~/pages/DemandForecastDaily/models/slotValueLimit'
import type { TargetScope } from '~/pages/DemandForecastDaily/models/types'

/** 確定ダイアログが submit する理由テキスト群 (ConfirmDemandDialog onConfirm)。 */
export type ConfirmRationale = {
  curveRationale?: string
  maxPowerRationale?: string
  minPowerRationale?: string
  numericalBasis?: string
  freeNote?: string
}

const MANUAL_CONFIRM_BLOCK_KEYS: Record<ManualConfirmationBlock, string> = {
  [ManualConfirmationBlock.UNSPECIFIED]:
    'page:demandForecastDaily.confirm.groupForecastBlocked',
  [ManualConfirmationBlock.CAPABILITY_DISABLED]:
    'page:demandForecastDaily.confirm.groupForecastBlocked',
  [ManualConfirmationBlock.MISSING_OR_UNSAVED_SOURCE]:
    'page:demandForecastDaily.confirm.serverError.reason.draftNotSaved',
  [ManualConfirmationBlock.INCOMPLETE_AFTER_GC]:
    'page:demandForecastDaily.confirm.serverError.reason.slotNotFilled',
  [ManualConfirmationBlock.UNATTRIBUTED_REMAINING]:
    'page:demandForecastDaily.confirm.serverError.reason.unattributedRemaining',
  [ManualConfirmationBlock.CASE_NOT_EDITABLE]:
    'page:demandForecastDaily.confirm.serverError.reason.caseNotEditable',
  [ManualConfirmationBlock.PARTITION_UNAVAILABLE]:
    'page:demandForecastDaily.confirm.groupForecastBlocked',
}

export type UseForecastPageModelParams = {
  page: DemandForecastDailyPage | undefined
  caseId: string | undefined
  selectedAreaCode: AreaCode | undefined
  section: WorkflowSection
  targetDate: ProtoDate | undefined
  gcKoma: number
  /** BG 全体ビューの配下小売一覧 (page?.retailers ?? [])。 */
  retailers: DemandForecastDailyPage['retailers']
  /** read-only (需要想定確定 / 完了 / 削除済み)。 */
  isCompleted: boolean
  /** wfCase が需要想定の確定と確定日時を返している。 */
  isConfirmedOnServer: boolean
  /** 需要想定が業務確定済みで、page が凍結された確定値を返す。 */
  isFrozen?: boolean
  isPageTransitioning?: boolean
  /** ページ本体に渡すのと同じ過去実績の日付 (小売の内訳もこの日付で読む)。 */
  pastActualDates: ProtoDate[]
  refetchPage: () => Promise<RefetchDemandForecastDailyPageResult>
}

export type UseForecastPageModelResult = ForecastGates & {
  // 配下 editor (`useForecastDraftEditor`) を view 向け projection
  // (`ForecastEditorViewModel`) に絞った単一 nested value として公開する。従来は
  // editor の内部形状 (19 field) を本 hook 自身の interface に Pick で素通し
  // 再エクスポートしており、editor の result 型が変わると public interface も churn
  // していた (seam leakage)。projection 型を editor 側に co-locate することで、
  // editor の field rename は projection の更新で閉じ、本 interface
  // (editor: ForecastEditorViewModel の 1 行) は churn しない。かつ view には
  // 表示 state / buffer-write ハンドラだけを見せ、page-model が wrap している
  // raw write 入口 (executeSaveAll 等) / fallback 前の raw 値 (saveStates 等) は
  // 到達不能に保つ (orchestration 迂回を型で塞ぐ)。index は model.editor から読む。
  editor: ForecastEditorViewModel
  // selection state
  targetScope: TargetScope
  selectedRetailerBusinessCd: string | undefined
  dragMode: 0 | 1
  setDragMode: Dispatch<SetStateAction<0 | 1>>
  onRetailerChange: (retailerBusinessCd: string) => void
  onTargetScopeChange: (scope: TargetScope) => void
  // 小売の内訳とグループタブ
  breakdown: RetailerBreakdownView
  breakdownLeaves: BreakdownLeafView[] | undefined
  breakdownTabKey: string
  onBreakdownTabSelect: (tabKey: string) => void
  /** 表示中のグループ・その他のタブ。合計タブ (今までどおりの小売) は undefined。 */
  breakdownLeaf: BreakdownLeafView | undefined
  /**
   * グループ別の小売のために確定できない理由 (backend の page の値)。undefined なら
   * グループ別の小売に由来する止め理由は無い。
   */
  confirmBlock: DemandForecastConfirmBlock | undefined
  manualConfirmDisabledReasonKey: string | undefined
  // scope
  isSingleRetailerBg: boolean
  hasMultipleRetailers: boolean
  canMutateTargetDate: boolean
  /** 対象日の表示ラベル (YYYY/MM/DD)。保存・確定の文言と画面表示で共有する。 */
  targetDateLabel: string
  // write orchestration
  handleSave: () => void
  handleSaveAndLeave: () => Promise<void>
  handleDiscardAndLeave: () => void
  handleResolveConflict: () => void
  handleConfirmExecute: (rationale: ConfirmRationale) => Promise<void>
  // leave guard
  leaveGuardBlocked: boolean
  leaveGuardCancel: () => void
  isLeaveSaving: boolean
  // confirm dialog + result
  isConfirmOpen: boolean
  openConfirmDialog: () => void
  closeConfirmDialog: () => void
  isConfirming: boolean
  confirmError: ReturnType<typeof useConfirmDemandForecast>['error']
  confirmResultAt: Date | undefined
  closeConfirmResult: () => void
  confirmedIndicatorAt: Date | undefined
  hasUnattributedRemaining: boolean
  isUnattributedGuardOpen: boolean
  closeUnattributedGuard: () => void
  // rescale modal
  isRescaleOpen: boolean
  openRescale: () => void
  closeRescale: () => void
}

export function useForecastPageModel(
  params: UseForecastPageModelParams,
): UseForecastPageModelResult {
  const {
    page,
    caseId,
    selectedAreaCode,
    section,
    targetDate,
    gcKoma,
    retailers,
    isCompleted,
    isConfirmedOnServer,
    isFrozen,
    isPageTransitioning = false,
    pastActualDates,
    refetchPage,
  } = params

  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  // ── 編集 selection state ──────────────────────────────────────────
  //
  // **「対象」は AC-SC-031 (フィルタ選択状態の localStorage 保持) の対象に含めない。**
  // `shared-components.md` 機能詳細 5 が「フィルタバーを持つ画面は AC-SC-031 により
  // 保持を必須とする (画面個別の spec で改めて規定しない)」とするため、 除外の判断は
  // ここに残す。
  //
  // - **これは表示の絞り込みではなく編集対象の選択**で、 値によって破壊的操作の可否が
  //   変わる: 48 コマ直接編集・グラフ変更・最大電力補正 (AC-DFD-004c/004d)、
  //   簡易予測の一括取込 (AC-DFD-033/033a)、 参照列の採用 (AC-DFD-047)。
  //   AC-DFD-041 は BG 切替を「ページ離脱・タブ切替・断面切替」と同列の離脱イベントと
  //   して未保存 3 択ダイアログの対象にしており (実装も `leaveGuard.guardAction()`
  //   経由)、 AC-SC-031 の User Story「フィルタボタンで表示データを絞り込みたい」が
  //   指す対象ではない
  // - 同じ画面に**失敗コストの大きい選択は都度既定へ復帰させる**確立済みの判断がある。
  //   AC-DFC-008b は気象観測地点の一時切替を別画面移動で組織共通設定へ戻す。理由も
  //   spec 本文に明記されている (「意図せず別地点のまま別断面の想定を進めない」)。
  //   既定の BG 全体は配下複数なら直接編集不可 (AC-DFD-004d) = 安全側で、 保持すると
  //   再訪時にいきなり**編集可能な小売スコープ**で開く
  // - 候補 (retailers) はケース・対象日ごとの応答由来で、 対象日時点で失効・適用前の
  //   BG / 小売は選択肢から外れる (AC-DFC-001)。 候補外コードを復元すると select は
  //   空表示なのに本体は黙って BG 合計へフォールバックする
  const [targetScope, setTargetScope] = useState<TargetScope>('BG_TOTAL')
  const [selectedRetailerBusinessCd, setSelectedRetailerBusinessCd] = useState<
    string | undefined
  >(undefined)
  // 補正モード (design L742-756): 0=編集コマのみ変更, 1=編集コマ以降へ比例配分。
  const [dragMode, setDragMode] = useState<0 | 1>(0)

  // AC-004c/004d: 表示用に BG 全体ビューと小売数を組み合わせる。
  const { isSingleRetailerBg, hasMultipleRetailers } = deriveRetailerScope(
    targetScope,
    retailers,
  )

  // AC-DFD-054a: 集計表示で内訳の合計が 0 のコマ (全社 0、または正負が打ち消し合って 0)
  // を変えようとした時の通知 (2026-10-13 PdM 判断で文言を「合計が 0」に統一)。
  const handleAggregateZeroRejected = useCallback(
    (komas: number[]): void => {
      appToaster.create({
        type: 'warning',
        title: t(
          hasGroupForecastRetailer(page?.retailers ?? [])
            ? 'page:demandForecastDaily.koma.aggregateReverted'
            : 'page:demandForecastDaily.koma.aggregateZeroRejected',
          { count: komas.length },
        ),
      })
    },
    [t, page],
  )

  // ── 小売の内訳とグループタブ ─────────────────────────────────────
  // 内訳は対象が小売の時だけ読む。タブは小売を替えると合計へ戻る。
  // グループ別の小売では、選んだタブの葉を editor が入力する。
  const breakdownRetailerBusinessCd =
    targetScope === 'RETAILER' ? selectedRetailerBusinessCd : undefined
  const selectedRetailer = page?.retailers.find(
    (r) => r.retailerBusinessCd === breakdownRetailerBusinessCd,
  )
  const pageBreakdownLeaves = breakdownLeavesFromPage(selectedRetailer)
  const actualBreakdown = useRetailerBreakdown({
    caseId,
    areaCode: selectedAreaCode,
    targetDate,
    retailerBusinessCd: breakdownRetailerBusinessCd,
    pastActualDates,
    pageHasGroupForecast:
      breakdownRetailerBusinessCd === undefined
        ? undefined
        : pageBreakdownLeaves !== undefined,
  })
  const [breakdownTabKey, selectBreakdownTab] = useBreakdownTabSelection(
    breakdownRetailerBusinessCd,
  )
  // page の下書きが入力 scope の SoT。実績 RPC は同じ葉の実績だけを補い、失敗時は
  // error を表示しつつ葉を空のまま残す。page 未着時は RPC の loading を保つが、古い
  // RPC の byGroup 応答だけから入力タブを復元しない。
  const breakdownLeaves =
    pageBreakdownLeaves === undefined
      ? undefined
      : joinBreakdownActuals(pageBreakdownLeaves, actualBreakdown)
  const breakdown: RetailerBreakdownView =
    pageBreakdownLeaves === undefined && actualBreakdown.kind === 'byGroup'
      ? { kind: 'direct', groupMissing: false }
      : actualBreakdown
  const breakdownLeaf = breakdownLeaves?.find(
    (leaf) => leaf.key === breakdownTabKey,
  )

  // ── 編集グリッド中核ロジック (editor hook を配下に compose) ──────────
  const draftEditor = useForecastDraftEditor({
    page,
    isFrozen,
    caseId,
    selectedAreaCode,
    section,
    targetDate,
    selectedRetailerBusinessCd,
    // 内訳に無いタブ (読み直しの失敗等) は合計として扱う (表示の過去実績と同じ葉を入力する)。
    breakdownTabKey: breakdownLeaf?.key ?? BREAKDOWN_TOTAL_TAB,
    targetScope,
    gcKoma,
    dragMode,
    refetchPage,
    onAggregateZeroRejected: handleAggregateZeroRejected,
    onAggregateError: (error) =>
      appToaster.create({
        type: 'error',
        title:
          error === undefined
            ? t('page:demandForecastDaily.koma.aggregatePreviewError')
            : (resolveAggregatePreviewDetailedErrorMessage(error, t) ??
              t('page:demandForecastDaily.koma.aggregatePreviewError')),
        description: t(
          'page:demandForecastDaily.koma.aggregatePreviewErrorDescription',
        ),
      }),
  })

  // orchestration (gate 派生 / write / leaveGuard / confirm) が読む editor 内部値
  // だけを取り出す。表示 state / buffer-write ハンドラは素通しせず draftEditor を
  // 丸ごと editor として公開する (return の editor: draftEditor)。
  const {
    editRows,
    hasUnattributedRemaining,
    effectiveWriteTarget,
    aggregateEdit,
    aggregateBaselineSlots,
    unattributedSlots,
    unattributedBaselineSlots,
    saveStates,
    activeEditRows,
    bgTotalLiveSlots,
    currentEditRow,
    currentDirtyKomas,
    currentRowDirty,
    bgTotalDirtyKomas,
    anyDirty,
    anyConflict,
    currentSaveState,
    executeSaveAll,
    discardAllDirty,
    resolveConflicts,
  } = draftEditor
  // 過去日 write-gate の表示側 flag。canMutate は実時刻 (now()) を読むため毎レンダー
  // 評価する (useMemo しない: 画面を開いたまま JST 0:00 を跨いだ直後の再レンダーで
  // 過去日へ即時追従させる、useJstMidnightTick と対)。
  const canMutateTargetDate = canMutate(targetDate)
  const confirmationContext = JSON.stringify([
    caseId,
    selectedAreaCode,
    section,
    targetDate,
    isCompleted,
  ])
  const contextVersion = useRef({ key: confirmationContext })
  if (contextVersion.current.key !== confirmationContext) {
    contextVersion.current = { key: confirmationContext }
  }
  const inactiveConflictKeys = Object.entries(saveStates)
    .filter(
      ([key, state]) =>
        key !== UNATTRIBUTED_SAVE_STATE_KEY &&
        state === 'conflict' &&
        activeEditRows[key] === undefined,
    )
    .map(([key]) => key)
  const hasInactiveConflict = inactiveConflictKeys.length > 0
  const selectedConflictKeys = Object.entries(activeEditRows)
    .filter(
      ([key, row]) =>
        row.retailerBusinessCd === selectedRetailerBusinessCd &&
        saveStates[key] === 'conflict',
    )
    .map(([key]) => key)

  // ── gate 群 (pure fn へ委譲、mount 無しで unit-addressable) ───────────
  const gates = deriveForecastGates({
    targetScope,
    hasUnattributedRemaining,
    isRetailerEditableTarget: effectiveWriteTarget.scope === 'RETAILER',
    aggregateEdit,
    aggregateBaselineSlots,
    unattributedSlots,
    unattributedBaselineSlots,
    bgTotalLiveSlots,
    currentEditRow,
    editRows,
    isCompleted,
    canMutateTargetDate,
    bgHasGroupForecast: hasGroupForecastRetailer(page?.retailers ?? []),
    // page がグループ別と返した小売。内訳がグループ別のまま page が一括を返した時 (応答の間に
    // モードが変わった) の葉のタブも、入力する行が無いのでグループ別として止める。
    isGroupForecastRetailer:
      targetScope === 'RETAILER' &&
      (breakdownLeaf !== undefined ||
        page?.retailers.find(
          (r) => r.retailerBusinessCd === selectedRetailerBusinessCd,
        )?.byGroup !== undefined),
    gcKoma,
    currentDirtyKomas,
    currentRowDirty,
    bgTotalDirtyKomas,
    anyDirty,
    currentSaveState:
      targetScope === 'RETAILER' && currentEditRow === undefined
        ? selectedConflictKeys.length > 0
          ? 'conflict'
          : undefined
        : currentSaveState,
    anyConflict,
    hasInactiveConflict,
  })

  // ── 未保存離脱ガード ──────────────────────────────────────────────
  const leaveGuard = useUnsavedGuard({
    isDirty: anyDirty,
    shouldBlockAction: () => anyDirty,
  })
  const [isLeaveSaving, setIsLeaveSaving] = useState(false)

  // ── 過去日 write-seam の単一 constructor ─────────────────────────────
  // write orchestration 入口 (保存 / 保存して移動) はこの HOF 経由でのみ構築し、
  // canMutate(targetDate) の呼び出し口を本 hook 内の 1 箇所に閉じる (旧: index の
  // 4 seam に分散していた convention 依存を排除)。canMutate は返す closure 内で
  // 呼ぶため実行時の実時刻で評価される (日跨ぎ残存経路対策)。
  const guardWrite = useCallback(
    (fn: () => void | Promise<void>, onBlocked?: () => void) =>
      async (): Promise<void> => {
        if (targetScope === 'UNATTRIBUTED' || !canMutate(targetDate)) {
          onBlocked?.()
          return
        }
        await fn()
      },
    [targetDate, targetScope],
  )

  const targetDateLabel = formatProtoDate(targetDate)

  // AC-DFC-EDIT-005: 負の値のコマを含めて保存・確定したら、保存・確定を行ったうえで
  // 警告する (複数コマは並べる)。
  const warnNegativeKomas = useCallback(
    (komas: number[]): void => {
      if (komas.length === 0) return
      appToaster.create({
        type: 'warning',
        title: formatNegativeWarning(komas, t, targetDateLabel),
      })
    },
    [t, targetDateLabel],
  )

  // 保存・「保存して移動」共通の結果処理。上限超過 (AC-DFC-EDIT-004) は先頭を title、
  // 残りを description に並べたエラー、送信後の失敗は errorTitle のエラー、負の値は
  // 警告を出す。全て成功した時だけ true を返す。
  const reportSaveResult = useCallback(
    (result: SaveAllResult, errorTitle: string): boolean => {
      if (result.kind === 'limitRejected') {
        const [title, ...rest] = formatLimitViolationLines(
          result.violations,
          t,
          targetDateLabel,
        )
        appToaster.create({
          type: 'error',
          title,
          description: rest.length > 0 ? rest.join('\n') : undefined,
        })
        return false
      }
      if (result.kind === 'notSent' || !result.ok) {
        appToaster.create({ type: 'error', title: errorTitle })
      }
      if (result.kind === 'notSent') return false
      warnNegativeKomas(result.negativeKomas)
      return result.ok
    },
    [t, targetDateLabel, warnNegativeKomas],
  )

  // AC-038/039/040/042: 「保存」。dirty な全小売分を保存し、全成功時のみ success
  // toast、1 つでも失敗すれば error toast。過去日は guardWrite が no-op にする
  // (executeSaveAll 内の gate は write seam の二重防御として残る)。KomaCard.onSave は
  // `() => void` を期待するため guardWrite の Promise を fire-and-forget で捨てる
  // (旧 index の handleSave と同型)。
  const transitionRun = useRef(false)
  const ordinarySaveInFlight = useRef(false)
  const handleSave = useCallback((): void => {
    if (transitionRun.current) return
    void guardWrite(async () => {
      transitionRun.current = true
      ordinarySaveInFlight.current = true
      try {
        const ok = reportSaveResult(
          await executeSaveAll(),
          t('page:demandForecastDaily.save.error'),
        )
        if (ok) {
          showSuccessToast({
            title: t('page:demandForecastDaily.save.success'),
          })
          // AC-5: グループの合計を引き継いだ小売を保存すると確定できるかは backend が決めるので、
          // 確定の止め理由を読み直す (定期の再取得まで古い理由で確定を止め続けない)。
          if (
            hasGroupForecastRetailer(page?.retailers ?? []) ||
            page?.confirmBlock ===
              DemandForecastConfirmBlock.GROUP_TOTAL_NOT_SAVED
          )
            await refetchPage()
        }
      } finally {
        ordinarySaveInFlight.current = false
        transitionRun.current = false
      }
    })()
  }, [
    guardWrite,
    executeSaveAll,
    reportSaveResult,
    showSuccessToast,
    t,
    page,
    refetchPage,
  ])

  // AC-07: 「保存して移動」。保存成功で proceed、失敗 (上限超過を含む) は離脱を取り消して
  // 留まる。過去日は通信・トーストなしで離脱をキャンセルする (onBlocked = leaveGuard.cancel)。
  const handleSaveAndLeave = useMemo(
    () =>
      guardWrite(
        async () => {
          if (transitionRun.current && !ordinarySaveInFlight.current) return
          transitionRun.current = true
          setIsLeaveSaving(true)
          try {
            const ok = reportSaveResult(
              await executeSaveAll(),
              t('page:demandForecastDaily.leaveGuard.saveError'),
            )
            if (ok) {
              leaveGuard.proceed()
            } else {
              leaveGuard.cancel()
            }
          } finally {
            transitionRun.current = false
            setIsLeaveSaving(false)
          }
        },
        () => {
          leaveGuard.cancel()
        },
      ),
    [guardWrite, executeSaveAll, leaveGuard, reportSaveResult, t],
  )

  const handleDiscardAndLeave = useCallback((): void => {
    discardAllDirty()
    leaveGuard.proceed()
  }, [discardAllDirty, leaveGuard])

  // BG_TOTAL(複数小売)ビューは currentEditRow が undefined のため、conflict な
  // 全小売を一括解決する (review 指摘 PR #3199)。
  const handleResolveConflict = useCallback((): void => {
    if (targetScope === 'RETAILER') {
      const selectedKeys =
        currentEditRow !== undefined
          ? [
              editUnitKey(
                currentEditRow.retailerBusinessCd,
                currentEditRow.unit,
              ),
            ]
          : selectedConflictKeys
      void resolveConflicts([
        ...new Set([...selectedKeys, ...inactiveConflictKeys]),
      ])
      return
    }
    const conflictedRetailerBusinessCds = Object.entries(saveStates)
      .filter(([, state]) => state === 'conflict')
      .map(([retailerBusinessCd]) => retailerBusinessCd)
    if (conflictedRetailerBusinessCds.length > 0)
      void resolveConflicts(conflictedRetailerBusinessCds)
  }, [
    currentEditRow,
    inactiveConflictKeys,
    resolveConflicts,
    saveStates,
    selectedConflictKeys,
    targetScope,
  ])

  const captureAndTransition = useCallback(
    async (action: () => void) => {
      if (transitionRun.current || draftEditor.hasInFlight) return
      transitionRun.current = true
      try {
        if (await draftEditor.captureAggregate()) action()
      } finally {
        transitionRun.current = false
      }
    },
    [draftEditor],
  )
  const groupRoute = hasGroupForecastRetailer(page?.retailers ?? [])
  const onBreakdownTabSelect = useCallback(
    (key: string) => {
      if (groupRoute) void captureAndTransition(() => selectBreakdownTab(key))
      else selectBreakdownTab(key)
    },
    [groupRoute, captureAndTransition, selectBreakdownTab],
  )

  // ── 小売 / scope 切替 (未保存ガード経由) ─────────────────────────────
  const onRetailerChange = useCallback(
    (retailerBusinessCd: string): void => {
      if (groupRoute) {
        void captureAndTransition(() => {
          setSelectedRetailerBusinessCd(retailerBusinessCd)
          setTargetScope('RETAILER')
        })
        return
      }
      if (targetScope === 'UNATTRIBUTED') {
        setSelectedRetailerBusinessCd(retailerBusinessCd)
        setTargetScope('RETAILER')
        return
      }
      // AC-05: 未保存の変更がある状態での小売切替は 3 択ダイアログ経由。
      leaveGuard.guardAction(() => {
        setSelectedRetailerBusinessCd(retailerBusinessCd)
        setTargetScope('RETAILER')
      })
    },
    [leaveGuard, targetScope, groupRoute, captureAndTransition],
  )

  const onTargetScopeChange = useCallback(
    (scope: TargetScope): void => {
      if (groupRoute) {
        void captureAndTransition(() => setTargetScope(scope))
        return
      }
      // AC-DFD-047c: 帰属不明の閲覧は同じ未保存sessionを保持する。
      if (scope === 'UNATTRIBUTED' || targetScope === 'UNATTRIBUTED') {
        setTargetScope(scope)
        return
      }
      // AC-05: BG全体⇄小売切替も同様にガードする。
      leaveGuard.guardAction(() => {
        setTargetScope(scope)
      })
    },
    [leaveGuard, targetScope, groupRoute, captureAndTransition],
  )

  // ── 確定 ─────────────────────────────────────────────────────────
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [isPreparingConfirmation, setIsPreparingConfirmation] = useState(false)
  const confirmationSnapshot = useRef<
    | {
        context: { key: string }
        request: ConfirmDemandForecastRequest
      }
    | undefined
  >(undefined)
  const currentConfirmEditorState = useRef({
    anyDirty,
    busy: draftEditor.hasInFlight,
  })
  currentConfirmEditorState.current = {
    anyDirty,
    busy: draftEditor.hasInFlight,
  }
  useEffect(() => {
    setIsConfirmOpen(false)
    confirmationSnapshot.current = undefined
  }, [confirmationContext])
  const [isUnattributedGuardOpen, setIsUnattributedGuardOpen] = useState(false)
  // 確定完了 結果モーダル (design L998-1020): 確定成功後の確定時刻。undefined 以外で開く。
  const [confirmResultAt, setConfirmResultAt] = useState<Date | undefined>(
    undefined,
  )
  // ダイアログを閉じた後も残す「確定済み」持続表示用 (ConfirmBar hint)。
  const [confirmedIndicatorAt, setConfirmedIndicatorAt] = useState<
    Date | undefined
  >(undefined)
  // 編集で未保存 (dirty) になったら「確定済み」表示をクリアする (Devin review 指摘)。
  useEffect(() => {
    if (anyDirty) {
      setConfirmedIndicatorAt(undefined)
    }
  }, [anyDirty])
  // AC-DFD-051: wfCase が確定を返した後は wfCase の確定日時を出すため、確定 RPC
  // 応答の日時を手放す (差戻し・完了取消で wfCase が未確定へ戻った時に出し続けない)。
  useEffect(() => {
    if (isConfirmedOnServer && confirmedIndicatorAt !== undefined) {
      setConfirmedIndicatorAt(undefined)
    }
  }, [isConfirmedOnServer, confirmedIndicatorAt])

  const {
    mutateAsync: confirmAsync,
    isPending: isConfirmPending,
    error: confirmError,
    reset: resetConfirmMutation,
  } = useConfirmDemandForecast(caseId ?? '')
  const isConfirming = isConfirmPending || isPreparingConfirmation

  const handleConfirmExecute = async (
    rationale: ConfirmRationale,
  ): Promise<void> => {
    const snapshot = confirmationSnapshot.current
    if (
      snapshot === undefined ||
      snapshot.context !== contextVersion.current ||
      isConfirming ||
      isCompleted ||
      isPageTransitioning ||
      page === undefined ||
      anyDirty ||
      draftEditor.hasInFlight
    )
      return
    if (
      targetScope === 'UNATTRIBUTED' ||
      selectedAreaCode === undefined ||
      targetDate === undefined
    )
      return
    // 過去日 write-seam (guardWrite と同一 predicate の inline 呼び出し): areaCode/
    // targetDate の precondition を先に評価する順序制約があるため HOF 化せずここで
    // canMutate を呼ぶ。確定は編集 hook を経由しない別 write seam のため入口に gate を
    // 残す。canMutate() は呼び出し時の実時刻で評価するため stale 化しない。ダイアログも
    // 閉じて UI と実行の乖離を防ぐ (PR #5066 review C-5)。
    if (!canMutate(targetDate)) {
      setIsConfirmOpen(false)
      return
    }
    try {
      const res = await confirmAsync(
        create(ConfirmDemandForecastRequestSchema, {
          ...snapshot.request,
          ...rationale,
        }),
      )
      setIsConfirmOpen(false)
      // AC-DFC-EDIT-005: 確定した全事業者の画面の値にある負のコマを警告する
      // (確定は止めない)。
      warnNegativeKomas(
        collectNegativeKomas(
          Object.values(editRows).map((row) => row.forecastSlots),
          gcKoma,
        ),
      )
      // 確定日時はサーバー由来 (response.confirmedAt) を使う。未設定時のみ now()。
      const confirmedAt =
        res.confirmedAt !== undefined
          ? timestampDate(res.confirmedAt)
          : serverNow()
      setConfirmResultAt(confirmedAt)
      setConfirmedIndicatorAt(confirmedAt)
    } catch (error) {
      // A concurrent allocation can leave a stale client view.  Treat the
      // backend guard exactly like the preflight guard rather than exposing a
      // second, generic confirmation error path.
      if (
        error instanceof ConnectError &&
        readAppErrorInfo(error, DemandForecastConfirmErrorReasonSchema)
          ?.reason === DemandForecastConfirmErrorReason.UNATTRIBUTED_REMAINING
      ) {
        setIsConfirmOpen(false)
        setIsUnattributedGuardOpen(true)
      }
      // AC-07: 全エラーは confirmError 経由で ConfirmDemandDialog に表示される。
    }
  }

  const openConfirmDialog = useCallback((): void => {
    if (targetScope === 'UNATTRIBUTED') return
    if (hasUnattributedRemaining) {
      setIsUnattributedGuardOpen(true)
      return
    }
    if (
      transitionRun.current ||
      isConfirming ||
      isCompleted ||
      isPageTransitioning ||
      page === undefined ||
      !canMutate(targetDate) ||
      caseId === undefined ||
      selectedAreaCode === undefined ||
      targetDate === undefined ||
      !draftEditor.canConfirm ||
      draftEditor.hasInFlight
    )
      return
    const context = contextVersion.current
    const request = create(ConfirmDemandForecastRequestSchema, {
      caseId,
      areaCode: selectedAreaCode,
      section,
      targetDate,
    })
    if (!groupRoute) {
      request.targets = Object.values(editRows)
        .filter((row) => row.unit.kind === 'retailer')
        .map((row) =>
          create(RetailerConfirmTargetSchema, {
            retailerBusinessCd: row.retailerBusinessCd,
            expectedUpdatedAt: row.draftUpdatedAt,
          }),
        )
      confirmationSnapshot.current = { context, request }
      setIsConfirmOpen(true)
      return
    }
    if (page?.manualConfirmation?.state.case !== 'ready') return
    transitionRun.current = true
    setIsPreparingConfirmation(true)
    void (async () => {
      try {
        const saved = await executeSaveAll()
        if (!reportSaveResult(saved, t('page:demandForecastDaily.save.error')))
          return
        const refreshed = await refetchPage()
        if (
          contextVersion.current !== context ||
          currentConfirmEditorState.current.anyDirty ||
          currentConfirmEditorState.current.busy ||
          refreshed.outcome !== 'success' ||
          refreshed.page.manualConfirmation?.state.case !== 'ready'
        )
          return
        const packet = refreshed.page.manualConfirmation.state.value
        if (
          packet.caseId !== caseId ||
          packet.areaCode !== selectedAreaCode ||
          packet.section !== section ||
          packet.targetDate?.year !== targetDate.year ||
          packet.targetDate.month !== targetDate.month ||
          packet.targetDate.day !== targetDate.day
        )
          return
        request.savedComposition = packet
        confirmationSnapshot.current = {
          context,
          // A poll may replace page data while the user enters their rationale.
          request: clone(ConfirmDemandForecastRequestSchema, request),
        }
        setIsConfirmOpen(true)
      } finally {
        transitionRun.current = false
        setIsPreparingConfirmation(false)
      }
    })()
  }, [
    hasUnattributedRemaining,
    targetScope,
    isConfirming,
    isCompleted,
    targetDate,
    caseId,
    selectedAreaCode,
    draftEditor,
    section,
    groupRoute,
    editRows,
    page,
    executeSaveAll,
    reportSaveResult,
    t,
    refetchPage,
    isPageTransitioning,
  ])

  // 閉じる時に mutation を reset (再オープン時の前回エラー残り防止)。ただし pending
  // 中 (isConfirming) の reset は observer を idle に戻して二重確定・エラー握り潰しを
  // 招くため行わない。
  const closeConfirmDialog = useCallback((): void => {
    setIsConfirmOpen(false)
    confirmationSnapshot.current = undefined
    if (!isConfirming) {
      resetConfirmMutation()
    }
  }, [isConfirming, resetConfirmMutation])

  const closeConfirmResult = useCallback((): void => {
    setConfirmResultAt(undefined)
  }, [])

  // ── 最大電力補正モーダル ──────────────────────────────────────────
  const [isRescaleOpen, setIsRescaleOpen] = useState(false)
  const openRescale = useCallback((): void => {
    setIsRescaleOpen(true)
  }, [])
  const closeRescale = useCallback((): void => {
    setIsRescaleOpen(false)
  }, [])

  return {
    // selection state
    targetScope,
    selectedRetailerBusinessCd,
    dragMode,
    setDragMode,
    onRetailerChange,
    onTargetScopeChange,

    breakdown,
    breakdownLeaves,
    breakdownTabKey,
    onBreakdownTabSelect,
    breakdownLeaf,
    // frozen page では manual_confirmation / confirm_block が absent (proto 契約)。旧サーバー扱いの GROUP_FORECAST を出さない。
    confirmBlock:
      groupRoute && !isFrozen
        ? page?.manualConfirmation?.state.case === 'ready'
          ? undefined
          : (page?.confirmBlock ?? DemandForecastConfirmBlock.GROUP_FORECAST)
        : page?.confirmBlock,
    manualConfirmDisabledReasonKey:
      groupRoute && page?.manualConfirmation?.state.case === 'blocked'
        ? (MANUAL_CONFIRM_BLOCK_KEYS[page.manualConfirmation.state.value] ??
          'page:demandForecastDaily.confirm.groupForecastBlocked')
        : undefined,

    // scope / gates
    isSingleRetailerBg,
    hasMultipleRetailers,
    canMutateTargetDate,
    ...gates,

    // 配下 editor の result を単一 nested value として公開 (素通し再エクスポート廃止)。
    // index は model.editor から表示 state / buffer-write ハンドラを読む。
    editor: draftEditor,

    targetDateLabel,
    // write orchestration
    handleSave,
    handleSaveAndLeave,
    handleDiscardAndLeave,
    handleResolveConflict,
    handleConfirmExecute,

    // leave guard
    leaveGuardBlocked: leaveGuard.isBlocked,
    leaveGuardCancel: leaveGuard.cancel,
    isLeaveSaving,

    // confirm dialog + result
    isConfirmOpen,
    openConfirmDialog,
    closeConfirmDialog,
    isConfirming,
    confirmError,
    confirmResultAt,
    closeConfirmResult,
    confirmedIndicatorAt,
    hasUnattributedRemaining,
    isUnattributedGuardOpen,
    closeUnattributedGuard: () => setIsUnattributedGuardOpen(false),

    // rescale modal
    isRescaleOpen,
    openRescale,
    closeRescale,
  }
}
